// Default Libraries
import { createActions, getActionIds, asyncAction } from '../helpers/redux';
import { normalize } from 'normalizr';
import { push } from 'react-router-redux';
import { List } from 'immutable';
import union from 'lodash/union';
import elementSchema from '../schemas/element';
import { RelationshipActions, relationshipSchema } from './relationship';

export const ElementActions = createActions({
  @asyncAction()
  async createElement(projectId, element) {
    return ({ client }) => {
      return client
        .post('/elements', {
          body: element,
        })
        .then(function(response) {
          return normalize(response, [elementSchema]);
        });
    };
  },

  @asyncAction()
  async renameElement(elementId, elementName) {
    return ({ client }) => {
      return client
        .patch(`/elements/${elementId}`, { body: { name: elementName } })
        .then(response => {
          return normalize(response, elementSchema);
        });
    };
  },

  @asyncAction()
  async deleteElement(element, redirectToHome) {
    return ({ dispatch, client, getState, Checkit }) => {
      return client.delete(`/elements/${element.id}`).then(response => {
        if (!redirectToHome) {
          dispatch(push(`/project/${element.projectId}/default`));
        }

        dispatch(ElementActions.clientDeleteElements(element.id));

        response['id'] = element.id;
        return response;
      });
    };
  },

  @asyncAction()
  async deleteElementMulti(projectId, elementIds) {
    return async ({ dispatch, client }) => {
      const response = await client.post(
        `/projects/${projectId}/elements/delete/bulk`,
        {
          body: { element_ids: elementIds },
        },
      );
      elementIds.forEach(elementId =>
        dispatch(ElementActions.clientDeleteElements(elementId)),
      );
      return response;
    };
  },

  @asyncAction()
  async copyElements(projectId, elementIds) {
    return async ({ dispatch, client }) => {
      const elementActions = getActionIds(ElementActions);
      const response = await client.post(
        `/projects/${projectId}/elements/copy/bulk`,
        { body: { element_ids: elementIds } },
      );
      response.forEach(element => {
        dispatch({
          type: elementActions.createElement.success,
          result: normalize([element], [elementSchema]),
          args: [element.project_id, element],
        });
      });
      return response;
    };
  },

  @asyncAction()
  async handleCycle(projectId, elementIds) {
    return async ({ dispatch, client }) => {
      const elementActions = getActionIds(ElementActions);
      const relationshipActions = getActionIds(RelationshipActions);
      const response = await client.post(
        `/projects/${projectId}/elements/handleCycle`,
        { body: { element_ids: elementIds } },
      );
      window.location.reload(false);
      // TODO don't try and manage the state updates - just refresh the page for now
      // response.forEach(element => {
      //   dispatch({
      //     type: elementActions.createElement.success,
      //     result: normalize([element], [elementSchema]),
      //     args: [element.project_id, element],
      //   });
      // });
      // response.relationships.forEach(relationship => {
      //   dispatch({
      //     type: relationshipActions.createRelationships.success,
      //     result: normalize([relationship], [relationshipSchema]),
      //     args: [relationship.project_id, relationship],
      //   });
      // });
      return response;
    };
  },

  // @asyncAction()
  // async getElements(projectId) {
  //   return ({ dispatch, client, getState }) => {
  //     return client
  //       .get(`/projects/${projectId}/elements`, {
  //         qs: {
  //           offset: getState().elementTabPage.getIn(['default', 'results'])
  //             .size,
  //           sortBy: getState().elementTabPage.getIn(['default', 'sortBy']),
  //         },
  //       })
  //       .then(function(response) {
  //         return response;
  //       });
  //   };
  // },

  @asyncAction()
  async updateElementType(elementId, elementTypeId) {
    return ({ client }) => {
      return client
        .patch(`/elements/${elementId}/type`, {
          body: { type_id: elementTypeId },
        })
        .then(response => {
          return normalize(response, elementSchema);
        });
    };
  },

  toggleState(key, elementId, reducerType, currentState) {
    return {
      key,
      elementId,
      reducerType,
      currentState,
    };
  },

  persistUpdateOrder(elementId, newOrder) {
    return ({ client }) =>
      client
        .put(`/elements/${elementId}/order`, {
          body: {
            order: newOrder,
          },
        })
        .then(response => normalize(response, elementSchema));
  },

  updateOrder(elementId, newOrder) {
    return ({ getState }) => {
      const dragElement = getState().elements.getIn(['elements', elementId]);
      const parentId = dragElement.get('parent_id');
      const childrenIds = getState().elementRelationships.getIn([
        'children',
        parentId,
      ]);
      const childrenElements = childrenIds.map(childElementId =>
        getState().elements.getIn(['elements', childElementId]),
      );

      const oldOrder = dragElement.get('order');
      const moveDown = newOrder > oldOrder;

      const a = childrenElements
        .filter(childElement => {
          const childElementOrder = childElement.get('order');
          return moveDown
            ? childElementOrder > oldOrder && childElementOrder <= newOrder // move down
            : childElementOrder < oldOrder && childElementOrder >= newOrder; // move up
        })
        .map(childElement => {
          const order = childElement.get('order');
          const updatedOrder = moveDown ? order - 1 : order + 1;
          return {
            id: childElement.get('id'),
            newOrder: updatedOrder,
          };
        });

      // + order = adjust everything > oldOrder <= new Order
      // - order = adjust everything > newOrder <= new Order

      return {
        elements: a.push({ id: elementId, newOrder }).toJS(),
      };
    };
  },

  clientDeleteElements(elementId) {
    return ({ getState }) => {
      let elementIds = [];

      function recurseChild(id) {
        let elementId = getState().elements.getIn(['elements', id], null);
        let children =
          typeof elementId.get('children') !== 'undefined'
            ? elementId.get('children')
            : List();

        if (children.size > 0) {
          children.forEach(child => {
            recurseChild(child);
          });
        }

        elementIds.push(id);
      }

      recurseChild(elementId);

      const relationshipMap = getState()
        .elementsRelationships.get('elementsRelationships')
        .filter((a, b, c) => {
          return elementIds.indexOf(b) > -1;
        });

      return {
        elementIds,
        relationshipMap,
      };
    };
  },

  @asyncAction()
  async getElement(elementId, options) {
    return ({ client }) => {
      return client
        .get(`/elements/${elementId}`, {
          qs: {
            fields: union(['type'], options.fields).join(','),
          },
        })
        .then(response => {
          response.relationships = [
            ...response.consumesFrom,
            ...response.providesTo,
          ];
          return normalize(response, elementSchema);
        });
    };
  },

  @asyncAction()
  async updateProperty(elementId, options) {
    return ({ client }) => {
      return client
        .patch(`/elements/${elementId}/props`, {
          body: {
            props: options.props,
          },
        })
        .then(response => {
          return {
            props: response,
            elementId,
          };
        });
    };
  },

  @asyncAction()
  async updatePropertyMulti(elementIds, options) {
    return ({ client, getState }) => {
      const projectId = getState().projects.get('currentProjectId');
      return client
        .patch(`/elements/props/bulk`, {
          body: {
            props: options.props,
            projectId,
            elementIds,
          },
        })
        .then(response => {
          return {
            props: response,
            elementIds,
          };
        });
    };
  },

  @asyncAction()
  async createCustomProperty(elementId, options) {
    return ({ client }) => {
      return client
        .post(`/elements/${elementId}`, {
          body: {
            customProps: options.customProps,
          },
        })
        .then(response => {
          // options.success();
          return {
            custom_props: response,
            elementId,
          };
        });
    };
  },

  @asyncAction()
  async updateCustomProperty(elementId, options) {
    return ({ client }) => {
      return client
        .patch(`/elements/${elementId}/customprops`, {
          body: {
            customProps: options.customProps,
          },
        })
        .then(response => {
          if (options.success) options.success();

          return {
            custom_props: response,
            elementId,
          };
        });
    };
  },

  @asyncAction()
  async getBulkElements(projectId, elementIds, options) {
    return async ({ client }) => {
      const response = await client.post('/elements/get', {
        body: {
          projectId,
          element_ids: elementIds,
          fields: options.fields,
        },
      });
      return normalize(response, [elementSchema]);
    };
  },
});



// WEBPACK FOOTER //
// ./src/actions/element.js